import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2MetaStrip,
  V2StatusStrip,
  ConfirmDialog,
  Icon,
  LoadingState,
  StatePanel,
  type V2BadgeTone,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  JOURNAL_STATUS_LABEL,
  type AccountListItem,
  type AccountListResponse,
  type JournalEntry,
  type JournalLine,
  type JournalStatus,
} from '../../lib/types/finance.js';
import { JournalAttachmentsCard } from './JournalAttachmentsCard.js';

/**
 * Status badge language of the V2 Finance frame «چرخهٔ وضعیت سند»
 * (finOverview): پیش‌نویس neutral · ارسال‌شده warn · تأییدشده info ·
 * ثبت قطعی success · (برگشت‌خورده / باطل‌شده) neutral.
 */
export const JOURNAL_STATUS_TONE: Record<JournalStatus, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'warn',
  approved: 'info',
  posted: 'success',
  tombstoned: 'neutral',
};

export const JOURNAL_STATUS_TEXT: Record<JournalStatus, string> = JOURNAL_STATUS_LABEL;

export function JournalStatusBadge({ status }: { readonly status: JournalStatus }): ReactNode {
  return (
    <V2Badge tone={JOURNAL_STATUS_TONE[status] ?? 'neutral'}>
      {JOURNAL_STATUS_TEXT[status] ?? status}
    </V2Badge>
  );
}

interface LineRow extends JournalLine {
  readonly id: string;
}

/** Ancestor names of an account, root first (never includes the account). */
function accountPath(accountId: string, byId: ReadonlyMap<string, AccountListItem>): string | null {
  const names: string[] = [];
  const seen = new Set<string>([accountId]);
  let parentId = byId.get(accountId)?.parentAccountId ?? null;
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) break;
    names.unshift(parent.name);
    parentId = parent.parentAccountId;
  }
  return names.length > 0 ? names.join(' › ') : null;
}

/**
 * The «سند» detail card of the V2 Finance journals tab: number + status badge
 * + actions, meta strip, lines grid with the «جمع سند · n» totals row and the
 * «سند تراز است» balance indicator. Used both as the right pane of the journals
 * master/detail (`embedded`) and as the body of the deep-link detail route.
 *
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html `/t/kimia/finance` journals tab (card
 *   «سند») + Sakhtar Finance.dc.html §03 JOURNAL DETAIL + REVERSAL
 * status: PARTIAL_REAL — omitted because the journal-entries DTO
 *   (apps/api/src/accounting/dto.ts `JournalEntryResponse`) carries no such
 *   field: «ثبت‌کننده» / «تأییدکننده» (no createdBy/approvedBy, only the
 *   opaque `backdatingAuthorizedBy` id), «دورهٔ مالی» (no fiscalPeriod on the
 *   journal), «چاپ سند» (no print/export endpoint — import/export is
 *   centralised in /data). Account path (مسیر حساب) is derived from the real
 *   `parentAccountId` chain of GET /finance/accounts.
 */
export function JournalDocumentCard({
  base,
  journalId,
  embedded = false,
  onMutated,
}: {
  readonly base: string;
  readonly journalId: string;
  /** Embedded in the list page: shows a link to the full page. */
  readonly embedded?: boolean;
  /** Called after a lifecycle action succeeded (so a list can refresh). */
  readonly onMutated?: () => void;
}): ReactNode {
  const { unit, label } = useAmountDisplay();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reversing, setReversing] = useState(false);
  const [reverseDate, setReverseDate] = useState('');
  const [reverseReason, setReverseReason] = useState('');

  const journal = useApiGet<JournalEntry>(
    `${base}/api/v1/accounting/journal-entries/${journalId}`,
    [base, journalId, refreshKey],
  );
  const accounts = useApiGet<AccountListResponse>(`${base}/api/v1/finance/accounts`, [base]);

  const accountsById = useMemo(() => {
    const map = new Map<string, AccountListItem>();
    if (accounts.status === 'ready') {
      for (const a of accounts.data.items) map.set(a.accountId, a);
    }
    return map;
  }, [accounts]);

  // Totals are derived from the returned lines as BigInt (never Number) so the
  // balance indicator reflects what is actually displayed.
  const sums = useMemo(() => {
    if (journal.status !== 'ready') return { debit: 0n, credit: 0n };
    const lines = journal.data.lines ?? [];
    return {
      debit: lines.reduce((s, l) => s + BigInt(l.debitRial), 0n),
      credit: lines.reduce((s, l) => s + BigInt(l.creditRial), 0n),
    };
  }, [journal]);

  async function runAction(path: string, failure: string) {
    setBusy(true);
    setError(null);
    try {
      await apiPost(path, undefined, { headers: { 'idempotency-key': crypto.randomUUID() } });
      setRefreshKey((k) => k + 1);
      onMutated?.();
    } catch (err) {
      setError(bannerMessageFrom(err, failure));
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmReverse() {
    setBusy(true);
    setError(null);
    try {
      await apiPost(
        `${base}/api/v1/accounting/journal-entries/${journalId}/reverse`,
        {
          ...(reverseDate ? { businessDate: reverseDate } : {}),
          ...(reverseReason.trim() ? { backdatingReason: reverseReason.trim() } : {}),
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setRefreshKey((k) => k + 1);
      onMutated?.();
    } catch (err) {
      setError(bannerMessageFrom(err, 'ثبت سند برگشت ناموفق بود.'));
    } finally {
      setBusy(false);
      setReversing(false);
      setReverseDate('');
      setReverseReason('');
    }
  }

  if (journal.status === 'loading') return <LoadingState />;
  if (journal.status === 'error') {
    const code = journal.error.status;
    return (
      <StatePanel
        variant={code === 403 ? 'permission_denied' : code === 404 ? 'not_found' : 'network_error'}
        title={code === 403 ? 'دسترسی ندارید' : code === 404 ? 'پیدا نشد' : 'قابل نمایش نیست'}
      />
    );
  }
  if (journal.status !== 'ready') return null;

  const j = journal.data;
  const number = j.officialDocumentNumber ?? j.provisionalNumber;
  const lines = j.lines ?? [];
  const balanced = sums.debit === sums.credit;
  const diff = sums.debit > sums.credit ? sums.debit - sums.credit : sums.credit - sums.debit;

  const columns: readonly V2GridColumn<LineRow>[] = [
    {
      key: 'row',
      header: 'ردیف',
      width: 56,
      render: (row) => <span className="sk-num">{row.rowNumber}</span>,
    },
    {
      key: 'account',
      header: 'حساب',
      width: 240,
      wrap: true,
      render: (row) => {
        const path = accountPath(row.accountId, accountsById);
        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                className="ltr sk-num"
                style={{
                  fontFamily: 'var(--sk-font-mono)',
                  fontSize: 12,
                  padding: '1px 6px',
                  borderRadius: 5,
                  background: 'var(--sk-bg-sunken)',
                }}
              >
                {row.accountCode ?? '—'}
              </span>
              <span style={{ fontWeight: 600 }}>{row.accountName ?? 'حساب نامشخص'}</span>
            </div>
            {path ? (
              <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>{path}</span>
            ) : null}
          </div>
        );
      },
      footer: <span>{`جمع سند · ${lines.length.toLocaleString('fa-IR')} ردیف`}</span>,
    },
    {
      key: 'desc',
      header: 'شرح ردیف',
      width: 180,
      wrap: true,
      muted: true,
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>{row.description ?? '—'}</span>
          {row.dimensions && row.dimensions.length > 0 ? (
            <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {row.dimensions.map((d) => (
                <span key={d.dimensionValueId} className="sk-tag" style={{ fontSize: 11 }}>
                  {d.dimensionName ?? 'تفصیلی'}: {d.valueName ?? d.valueCode ?? '—'}
                </span>
              ))}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'debit',
      header: amountColumnHeader('بدهکار', unit),
      align: 'end',
      width: 130,
      render: (row) => (
        <span className="sk-num" style={{ fontWeight: 600, color: 'var(--sk-debit)' }}>
          {BigInt(row.debitRial) > 0n ? formatRialFa(row.debitRial, unit) : '—'}
        </span>
      ),
      footer: (
        <span className="sk-num" style={{ color: 'var(--sk-debit)' }}>
          {formatRialFa(sums.debit.toString(), unit)}
        </span>
      ),
    },
    {
      key: 'credit',
      header: amountColumnHeader('بستانکار', unit),
      align: 'end',
      width: 130,
      render: (row) => (
        <span className="sk-num" style={{ fontWeight: 600, color: 'var(--sk-credit)' }}>
          {BigInt(row.creditRial) > 0n ? formatRialFa(row.creditRial, unit) : '—'}
        </span>
      ),
      footer: (
        <span className="sk-num" style={{ color: 'var(--sk-credit)' }}>
          {formatRialFa(sums.credit.toString(), unit)}
        </span>
      ),
    },
  ];

  const metaItems: { label: ReactNode; value: ReactNode }[] = [
    {
      label: 'تاریخ سند',
      value: <span className="sk-num">{displayJalaliDate(j.businessDate)}</span>,
    },
    { label: 'شمارهٔ موقت', value: <span className="ltr sk-num">{j.provisionalNumber}</span> },
    {
      label: 'شمارهٔ رسمی',
      value: <span className="ltr sk-num">{j.officialDocumentNumber ?? '—'}</span>,
    },
    { label: 'نوع سند', value: j.journalType },
    {
      label: 'منبع',
      value: (
        <>
          {j.sourceModule}
          {j.sourceReference ? <span className="ltr sk-num"> · {j.sourceReference}</span> : null}
        </>
      ),
    },
  ];
  if (j.backdatingReason) {
    metaItems.push({ label: 'دلیل ثبت با تاریخ گذشته', value: j.backdatingReason });
  }

  return (
    <>
      <V2Card>
        <V2CardHeader
          icon="finance"
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span>
                سند <span className="ltr sk-num">{number}</span>
              </span>
              <JournalStatusBadge status={j.status} />
              {j.reversalJournalId ? <V2Badge tone="neutral">برگشت‌خورده</V2Badge> : null}
            </span>
          }
          subtitle={j.description ?? 'سند حسابداری'}
          actions={
            <>
              {embedded ? (
                <Link
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  to={`${base}/finance/journals/${j.journalId}`}
                >
                  صفحهٔ سند
                </Link>
              ) : null}
              {j.status === 'draft' ? (
                <V2Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    void runAction(
                      `${base}/api/v1/finance/journals/${j.journalId}/submit`,
                      'ارسال برای تأیید ناموفق بود.',
                    )
                  }
                >
                  {busy ? 'در حال ارسال…' : 'ارسال برای تأیید'}
                </V2Button>
              ) : null}
              {j.status === 'submitted' ? (
                <V2Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    void runAction(
                      `${base}/api/v1/finance/journals/${j.journalId}/approve`,
                      'تأیید سند ناموفق بود.',
                    )
                  }
                >
                  {busy ? 'در حال تأیید…' : 'تأیید سند'}
                </V2Button>
              ) : null}
              {j.status === 'approved' ? (
                <V2Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    void runAction(
                      `${base}/api/v1/finance/journals/${j.journalId}/post`,
                      'ثبت نهایی سند ناموفق بود.',
                    )
                  }
                >
                  {busy ? 'در حال ثبت…' : 'ثبت نهایی سند'}
                </V2Button>
              ) : null}
              {j.status === 'posted' && !j.reversalJournalId ? (
                <V2Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setReversing(true)}
                  style={{ color: 'var(--sk-danger)' }}
                >
                  سند برگشتی
                </V2Button>
              ) : null}
              <Link
                to={`${base}/print/journals/${j.journalId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <V2Button variant="secondary" size="sm" type="button">
                  چاپ سند
                </V2Button>
              </Link>
            </>
          }
        />

        {j.status === 'posted' ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '10px 14px',
              background: 'var(--sk-success-soft)',
              borderBottom: '1px solid var(--sk-divider)',
            }}
          >
            <span style={{ color: 'var(--sk-success)', display: 'flex', paddingTop: 1 }}>
              <Icon name="shield" size="sm" />
            </span>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                این سند قطعی و تغییرناپذیر است
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.7, color: 'var(--sk-fg-muted)' }}>
                هیچ سطر یا مبلغی قابل ویرایش نیست. برای اصلاح، سند برگشت ثبت کنید تا اثر آن با سند
                متقابل خنثی شود.
              </div>
            </div>
          </div>
        ) : null}

        {j.reversalJournalId ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              background: 'var(--sk-warn-soft)',
              borderBottom: '1px solid var(--sk-divider)',
            }}
          >
            <span style={{ color: 'var(--sk-warn)', display: 'flex' }}>
              <Icon name="arrowDown" size="sm" />
            </span>
            <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>
              این سند برگشت خورده —{' '}
              <Link
                to={`${base}/finance/journals/${j.reversalJournalId}`}
                style={{ fontWeight: 600 }}
              >
                مشاهدهٔ سند برگشت
              </Link>
            </span>
          </div>
        ) : null}

        <V2MetaStrip items={metaItems} />

        {lines.length > 0 ? (
          <>
            <V2DataGrid<LineRow>
              bare
              densitySwitch={false}
              columnChooser={false}
              pageSize={0}
              maxHeight="none"
              columns={columns}
              rows={lines.map((l) => ({ ...l, id: l.journalLineId }))}
              rowKey={(r) => r.id}
              ariaLabel="اقلام سند"
              legendNote={`مبالغ به ${label}`}
            />
            <V2StatusStrip
              tone={balanced ? 'success' : 'danger'}
              title={balanced ? 'سند تراز است' : 'سند تراز نیست'}
              description={
                <span>
                  اختلاف بدهکار و بستانکار:{' '}
                  <span className="sk-num">{formatRialFa(diff.toString(), unit)}</span> {label}
                </span>
              }
            />
          </>
        ) : (
          <StatePanel variant="empty" title="سطری ثبت نشده" />
        )}

        {error ? (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '10px 14px',
              borderTop: '1px solid var(--sk-divider)',
              background: 'var(--sk-danger-soft)',
            }}
          >
            <span style={{ color: 'var(--sk-danger)', display: 'flex' }}>
              <Icon name="x" size="sm" />
            </span>
            <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{error}</span>
          </div>
        ) : null}
      </V2Card>

      <JournalAttachmentsCard base={base} journalId={j.journalId} posted={j.status === 'posted'} />

      {reversing ? (
        <ConfirmDialog
          open
          title={`ثبت سند برگشت برای ${number}؟`}
          message={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span>
                سند قطعی تغییر نمی‌کند. یک سند تازه با ارقام جابه‌جا ساخته می‌شود و اثر مالی هر دو
                خنثی می‌شود.
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>
                  تاریخ سند برگشت (اختیاری — پیش‌فرض امروز)
                </label>
                <JalaliDateInput value={reverseDate} onChange={setReverseDate} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>
                  دلیل ثبت با تاریخ گذشته (فقط در صورت ثبت با تاریخ گذشته لازم است)
                </label>
                <textarea
                  className="sk-textarea"
                  placeholder="مثلاً: ثبت اشتباه حساب طرف مقابل"
                  value={reverseReason}
                  onChange={(e) => setReverseReason(e.target.value)}
                />
              </div>
            </div>
          }
          danger
          confirmLabel="ساخت سند برگشت"
          cancelLabel="انصراف"
          onConfirm={onConfirmReverse}
          onCancel={() => {
            setReversing(false);
            setReverseDate('');
            setReverseReason('');
          }}
        />
      ) : null}
    </>
  );
}
